import { AlertTriangle, ChevronDown } from 'lucide-react';
import { CompletedIcon, InProgressIcon, PendingIcon } from './TaskIcon';
import { isAiWorkItemType } from '@/lib/task-like-list-items';

/**
 * Status values supported by the task status badge component.
 */
export type TaskStatusBadgeValue =
  | 'PENDING'
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'FAILED'
  | 'CHAT';

/**
 * Reusable status badge view for task statuses.
 * Renders icon + colors based on the provided status.
 *
 * @param props - Badge display props.
 * @returns A status badge UI element.
 */
export function StatusBadge({
  statusLabel,
  statusTextOverride,
  className = '',
  showDropdownIndicator = false,
  itemType,
}: StatusBadgeProps) {
  const normalizedStatus = normalizeStatus(statusLabel);
  const renderedStatusText = statusTextOverride || normalizedStatus.replace(/_/g, ' ');
  const shouldSpinIcon = normalizedStatus === 'IN_PROGRESS' && isAiWorkItemType(itemType);

  return (
    <div
      className={`inline-flex max-w-full items-center gap-1 rounded-full bg-[#EEECE5] px-2.5 py-1 transition-colors duration-200 hover:bg-[#E4E0D6] ${className}`}
    >
      <span
        className={`inline-flex rounded-full p-[2px] ${getStatusIconToneClassName(normalizedStatus)} ${shouldSpinIcon ? 'animate-[spin_3s_linear_infinite_reverse]' : ''}`}
      >
        {getStatusIcon(normalizedStatus)}
      </span>
      <span className="line-clamp-3 text-ellipsis text-xs font-medium text-[#262626B2] md:text-sm">
        {renderedStatusText}
      </span>
      {showDropdownIndicator ? (
        <ChevronDown className="h-3 w-3 shrink-0 md:text-sm rotate-0 text-xs  font-medium transition-transform duration-300 ease-in-out will-change-transform group-data-[state=open]:rotate-180 motion-reduce:transition-none" />
      ) : null}
    </div>
  );
}

function normalizeStatus(statusLabel: string): TaskStatusBadgeValue {
  const normalized = String(statusLabel || '').toUpperCase().trim();
  if (
    normalized === 'PENDING' ||
    normalized === 'IN_PROGRESS' ||
    normalized === 'COMPLETED' ||
    normalized === 'FAILED' ||
    normalized === 'CHAT'
  ) {
    return normalized;
  }
  return 'PENDING';
}

function getStatusIcon(status: TaskStatusBadgeValue) {
  if (status === 'COMPLETED') {
    return CompletedIcon;
  }
  if (status === 'IN_PROGRESS' || status === 'CHAT') {
    return InProgressIcon;
  }
  if (status === 'FAILED') {
    return <AlertTriangle className="self-center h-2.5 w-2.5 md:h-3 md:w-3" />;
  }
  return PendingIcon;
}

function getStatusIconToneClassName(status: TaskStatusBadgeValue): string {
  if (status === 'COMPLETED') {
    return '';
  }
  if (status === 'IN_PROGRESS') {
    return '';
  }
  if (status === 'FAILED') {
    return 'bg-[#DC262626] text-[#DC2626]';
  }
  return '';
}

type StatusBadgeProps = {
  statusLabel: string;
  statusTextOverride?: string;
  className?: string;
  showDropdownIndicator?: boolean;
  /** Item type of the underlying task; spins the icon for AI-driven work (AI_TASK/AI_TRANSCODE_TASK) that's in progress. */
  itemType?: string | null;
};
